import { HTMLAttributes, useState, useRef, memo, useCallback } from 'react';
import { useSetCurrentConversationIdValue } from 'hooks/business/conversation';
import styled from '@emotion/styled';
import { useMemoizedFn, useMount } from 'ahooks';
import {
  useBatchForwardEnable,
  useBatchForwardLoading,
} from 'hooks/business/conversationsHooks/useConversationBatchForward';
import { Spin } from 'antd';
import { useTranslate } from '@tolgee/react';
import ForwardDlg from './ForwardDlg';
import Search from './Search';
import BatchForward from './BatchForward';
import { useReceptionConfig } from 'views/Setting/ConversationSetting/useReceptionConfig';
import { LoadingOutlined } from '@ant-design/icons';
import { SplitLine } from 'components/SplitLine';
import { useBootSyncTalkIds } from 'hooks/business/useSyncTalkIds';
import useModal from 'hooks/useModal';
import { ConversationCategory } from './ConversationCategory';
import {
  useConversationCategory,
  useConversationCategoryEnable,
  useSetConversationCategory,
} from 'hooks/business/conversationsHooks/useConversationCategory';
import { ConversationListSection } from './ConversationListSection';

const splitCacheKey = 'convListSplitLine';

const WRAPPER_MAX_WIDTH = 360;
const WRAPPER_MIN_WIDTH = 260;

const defaultWrapperWidth = localStorage.getItem(splitCacheKey) || WRAPPER_MIN_WIDTH;

const Container = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: ${defaultWrapperWidth}px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: ${WRAPPER_MIN_WIDTH}px;
  background-color: #fff;
  border-right: 1px solid #f0f2f5;
`;
const SpinContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  background: rgba(35, 36, 38, 0.5);
`;

export interface Props extends HTMLAttributes<HTMLDivElement> {}

function ConversationMain(props: Props) {
  const { ...otherProps } = props;
  // const [visibleForwardDlg, setVisibleForwardDlg] = useState(false);
  const { t } = useTranslate();
  const {
    visible: forwardModalVisible,
    meta: forwardModalMeta,
    show: showForwardModal,
    hide: hideForwardModal,
  } = useModal<Conversation.Conversation>();

  useBootSyncTalkIds();

  const setCurrentConversationId = useSetCurrentConversationIdValue();
  const [forceUpdate, setForceUpdate] = useState(Date.now());
  const batchForwardEnable = useBatchForwardEnable();

  const conversationCategory = useConversationCategory();
  const setConversationCategory = useSetConversationCategory();

  const [cacheConvIdsMine, setCacheConvIdsMine] = useState<string[]>([]);
  const [cacheConvIdsAll, setCacheConvIdsAll] = useState<string[]>([]);

  const batchForwardLoading = useBatchForwardLoading();

  const conversationCategoryEnable = useConversationCategoryEnable();

  const convWraper = useRef<HTMLDivElement>(null);

  const reloadConversation = useMemoizedFn(() => {
    setForceUpdate(Date.now());
    setCurrentConversationId(null);
  });

  const handleCategoryChange = useCallback(
    (category: 'mine' | 'all') => {
      if (category === conversationCategory) return;
      setConversationCategory(category);
      // 切换分类时清除当前选中的会话
      setCurrentConversationId(null);
    },
    [conversationCategory, setConversationCategory, setCurrentConversationId],
  );

  const [mounted, setMounted] = useState(false);
  useMount(() => {
    setMounted(true);
  });

  return (
    <Container ref={convWraper} {...otherProps}>
      <SplitLine
        type="vertical"
        position="right"
        target={convWraper}
        max={WRAPPER_MAX_WIDTH}
        min={WRAPPER_MIN_WIDTH}
        onDone={(newValue) => {
          localStorage.setItem(splitCacheKey, newValue.toString());
        }}
      />
      {batchForwardLoading ? (
        <SpinContainer>
          <Spin tip={t('forward.batchLoading')} spinning={batchForwardLoading} />
        </SpinContainer>
      ) : null}

      {/* Search - 共享状态 */}
      <Search convWrap={(mounted ? convWraper.current : null) as HTMLDivElement} />

      {/* Category Tabs */}
      {conversationCategoryEnable && (
        <ConversationCategory category={conversationCategory} onChange={handleCategoryChange} />
      )}

      {/* Mine Section */}
      {conversationCategory === 'mine' && (
        <ConversationListSection
          category="mine"
          onForceUpdate={reloadConversation}
          forceUpdateKey={forceUpdate}
          convWraperRef={convWraper.current}
          onForward={showForwardModal}
          cacheConvIds={cacheConvIdsMine}
          setCacheConvIds={setCacheConvIdsMine}
        />
      )}

      {/* All Section */}
      {conversationCategory === 'all' && (
        <ConversationListSection
          category="all"
          onForceUpdate={reloadConversation}
          forceUpdateKey={forceUpdate}
          convWraperRef={convWraper.current}
          onForward={showForwardModal}
          cacheConvIds={cacheConvIdsAll}
          setCacheConvIds={setCacheConvIdsAll}
        />
      )}

      {batchForwardEnable ? (
        <BatchForward
          onForward={() => {
            showForwardModal();
          }}
          cacheConvIds={cacheConvIdsMine}
        />
      ) : null}
      {forwardModalVisible ? (
        <ForwardDlg conversation={forwardModalMeta} onClose={hideForwardModal} />
      ) : null}
    </Container>
  );
}

const Conversation = memo((props: Props) => {
  const receptionConfig = useReceptionConfig();
  return receptionConfig === null ? <LoadingOutlined /> : <ConversationMain {...props} />;
});

export default Conversation;
